import { useState } from 'react';
import { Coffee, Loader, X } from 'lucide-react';
import { DockGlyph } from './dock-icons';
import { CollectibleSprite } from './collectible-icons';
import { loreFor } from './collectible-lore';
import { deriveStats, STAT_LABELS } from '../../lib/collectible-stats';
import type { CollectionEntry } from '../../services/collection-service';

interface LootBagProps {
  nfts: any[];
  onRedeem: (nft: any) => void;
  pendingRedeemIds?: string[];
  collection?: CollectionEntry[];
  redemptionCount?: number;
  mintCounts?: Record<string, number>;
  resolveShopName?: (shopId: string) => string | null;
  embedded?: boolean; // kept for prop-surface stability — the PASS tab supplies its own layout now
}

// Rarity accents, coral palette: grey → teal → purple → gold, in ascending
// order. Distinct hues carry real information (rarity), so kept distinct
// rather than collapsed to one color.
const RARITY_BORDER: Record<string, string> = {
  common: 'border-hairline', uncommon: 'border-pixel-cyan',
  rare: 'border-pixel-purple', legendary: 'border-accent-alt',
};
const RARITY_TEXT: Record<string, string> = {
  common: 'text-body-grey', uncommon: 'text-pixel-cyan',
  rare: 'text-pixel-purple', legendary: 'text-gold-ink',
};
const RARITY_BAR: Record<string, string> = {
  common: 'bg-body-grey', uncommon: 'bg-pixel-cyan',
  rare: 'bg-pixel-purple', legendary: 'bg-accent-alt',
};

export default function LootBag({
  nfts, onRedeem, pendingRedeemIds = [], collection = [],
  redemptionCount = 0, mintCounts = {}, resolveShopName,
}: LootBagProps) {
  const [expanded, setExpanded] = useState<CollectionEntry | null>(null);
  const ownedCount = collection.filter(c => c.owned).length;

  return (
    <>
      {/* Redeemable vouchers — on-chain NFTs earned from item boxes, unchanged behavior */}
      <div className="bg-white pixel-corners p-[18px]">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-1">
          <span className="font-heading text-[9px] tracking-[1px] uppercase text-body-grey flex items-center gap-2">
            <DockGlyph icon="loot" className="w-3 h-3" /> {nfts.length} READY TO REDEEM
          </span>
          <span className="font-heading text-[9px] text-coral">
            FREE COFFEES CLAIMED: {redemptionCount}
          </span>
        </div>

        <div className="overflow-y-auto flex flex-col gap-3 max-h-40">
          {nfts.length === 0 ? (
            <p className="font-grotesk font-medium text-[13px] text-body-grey text-center py-2">
              No loot yet — keep brewing.
            </p>
          ) : (
            nfts.map((nft: any) => {
              const isPending = pendingRedeemIds.includes(nft.id);
              return (
                <button
                  key={nft.id}
                  onClick={() => !isPending && onRedeem(nft)}
                  disabled={isPending}
                  className={`flex items-center justify-between pixel-corners p-3 border-[3px] transition-colors ${
                    isPending
                      ? 'border-accent-alt bg-background cursor-wait'
                      : 'border-primary bg-white hover:bg-background cursor-pointer'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Coffee size={16} className="text-coral" />
                    <div className="text-left">
                      <span className="font-heading text-[9px] text-primary block">FREE COFFEE</span>
                      <span className="font-grotesk font-medium text-[11px] text-body-grey">
                        Lvl {nft.level_reached} &middot;{' '}
                        {new Date(nft.timestamp).toLocaleDateString('en-AU', { month: 'short', day: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  {isPending ? (
                    <span className="font-heading text-[8px] text-coral ml-2 flex items-center gap-1">
                      <Loader size={12} className="animate-spin" /> WAITING
                    </span>
                  ) : (
                    <span className="font-heading text-[8px] text-coral ml-2">REDEEM</span>
                  )}
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Item boxes — collection grid, sprites stay square inside rounded tiles */}
      {collection.length > 0 && (
        <div className="bg-white pixel-corners p-[18px]">
          <p className="font-heading text-[9px] tracking-[1px] uppercase text-body-grey mb-[14px]">
            ITEM BOXES &middot; {ownedCount} / {collection.length}
          </p>
          <div className="grid grid-cols-4 gap-[10px]">
            {collection.map(item => (
              <button
                key={item.id}
                onClick={() => item.owned && setExpanded(item)}
                className={`aspect-square pixel-corners border-[3px] p-1.5 flex flex-col items-center justify-center ${
                  item.owned
                    ? `${RARITY_BORDER[item.rarity]} bg-white cursor-pointer hover:bg-background`
                    : 'border-hairline bg-background opacity-60'
                }`}
              >
                {item.owned ? (
                  <span className={`w-full h-full ${RARITY_TEXT[item.rarity]}`}>
                    <CollectibleSprite icon={item.icon} className="w-full h-full" />
                  </span>
                ) : (
                  <span className="font-heading text-[10px] text-body-grey">???</span>
                )}
                {item.owned && (item.print_number || 1) > 1 && (
                  <span className="font-heading text-[8px] text-body-grey mt-0.5">PRINT #{item.print_number}</span>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Item card modal — game-style: sprite, stats, backstory, origin, circulation */}
      {expanded && (() => {
        const lore = loreFor(expanded.slug, expanded.description);
        const stats = deriveStats(expanded.slug, expanded.rarity);
        const mints = mintCounts[expanded.id];
        const originName = expanded.shop_id && resolveShopName ? resolveShopName(expanded.shop_id) : null;
        const originLine = expanded.shop_id
          ? `SHOP EXCLUSIVE${originName ? ` · ${originName.toUpperCase()}` : ''}`
          : 'FOUND AT ANY SHOP';
        return (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center safe-modal bg-black/70 backdrop-blur-sm"
            onClick={() => setExpanded(null)}
          >
            <div
              className={`w-full max-w-sm max-h-full overflow-y-auto bg-white pixel-corners border-[3px] ${RARITY_BORDER[expanded.rarity]} p-5 sm:p-8 relative`}
              onClick={e => e.stopPropagation()}
            >
              <button onClick={() => setExpanded(null)} className="absolute top-1 right-1 p-3 text-body-grey hover:text-primary">
                <X size={20} />
              </button>
              <div className={`w-24 h-24 mx-auto mb-6 ${RARITY_TEXT[expanded.rarity]}`}>
                <CollectibleSprite icon={expanded.icon} className="w-full h-full" />
              </div>
              <p className={`font-heading text-[9px] mb-2 text-center uppercase ${RARITY_TEXT[expanded.rarity]}`}>
                {expanded.rarity}{(expanded.print_number || 1) > 1 ? ` · PRINT #${expanded.print_number}` : ''}
              </p>
              <h3 className="font-grotesk font-bold text-[17px] text-primary text-center mb-4">{expanded.name}</h3>

              {/* Stat bars */}
              <div className="space-y-2 mb-5">
                {STAT_LABELS.map(label => (
                  <div key={label} className="flex items-center gap-2">
                    <span className="font-heading text-[7px] text-body-grey w-16 shrink-0">{label}</span>
                    <div className="flex-1 h-2 pixel-bar pixel-corners-sm [--bar-gap:#f2ead6] bg-tab-inactive overflow-hidden">
                      <div className={`h-full ${RARITY_BAR[expanded.rarity]}`} style={{ width: `${stats[label]}%` }} />
                    </div>
                    <span className="font-heading text-[8px] text-primary w-6 text-right">{stats[label]}</span>
                  </div>
                ))}
              </div>

              {/* Backstory */}
              <p className="font-grotesk font-medium text-[13px] text-body-grey leading-relaxed mb-4">{lore.backstory}</p>

              {/* Effect line */}
              <p className={`font-heading text-[8px] ${RARITY_TEXT[expanded.rarity]} border border-hairline pixel-corners px-2 py-2 mb-4 leading-relaxed`}>
                {lore.effect}
              </p>

              {/* Origin + circulation */}
              <div className="flex items-center justify-between gap-2 flex-wrap border-t border-hairline pt-3">
                <span className="font-heading text-[7px] text-body-grey">{originLine}</span>
                {typeof mints === 'number' && (
                  <span className="font-heading text-[7px] text-body-grey">
                    {mints} IN CIRCULATION
                  </span>
                )}
              </div>
              {expanded.earned_at && (
                <p className="font-heading text-[7px] text-body-grey mt-2">
                  EARNED {new Date(expanded.earned_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                </p>
              )}
            </div>
          </div>
        );
      })()}
    </>
  );
}
